import type {
  Run,
  ThreadMessage,
  ThreadMessagePage,
  ThreadSnapshot,
  TutorBlock,
} from "@orbin/contracts";
import { TutorBlockSchema } from "@orbin/contracts";
import { CHILD_CRISIS_TEXT, CHILD_REFUSAL_TEXT, CHILD_TRUSTED_ADULT_TEXT, CHILD_UNJUDGED_TEXT } from "../kids/safety.js";

/** The child's own chat: kids/chat/* with the child token. The server picks the Preá; no bot id is sent. */
export interface ChildChatApi {
  get(): Promise<ThreadSnapshot>;
  messages(input: { before?: number }): Promise<ThreadMessagePage>;
  send(input: { text: string; clientNonce?: string }): Promise<{ runId: string; seq: number }>;
  stop(): Promise<{ ok: true }>;
}

export type ChildChatItem = {
  id: string;
  seq: number;
  from: "child" | "preá";
  text: string;
  /** The tutor stage the server decided for this reply; null for the child's own messages and plain replies. */
  tutor: TutorBlock | null;
  /** A crisis reply: the card with the support lines stays until a line is tapped. */
  crisis: boolean;
  /** The gate refused the turn; the reply is the refusal sentence. */
  refusal: boolean;
};

const ACTIVE: ReadonlySet<Run["status"]> = new Set([
  "queued",
  "leased",
  "running",
  "waiting_input",
  "waiting_takeover",
]);

/** Whether a run is still producing a reply. */
export function runIsActive(run: Pick<Run, "status"> | null | undefined): boolean {
  return Boolean(run && ACTIVE.has(run.status));
}

/** The chat items of a thread page. Only server-decided signals mark a tutor, crisis or refusal reply. */
export function childChatItems(messages: readonly ThreadMessage[]): ChildChatItem[] {
  return messages
    .filter((message) => message.role === "user" || message.role === "bot")
    .map((message) => {
      const blocks = message.blocks as readonly { kind: string; text?: string }[];
      const text = blocks
        .filter((block) => block.kind === "text" && typeof block.text === "string")
        .map((block) => block.text)
        .join("\n\n");
      const tutorBlock = blocks.find((block) => block.kind === "tutor");
      const tutor = tutorBlock ? TutorBlockSchema.safeParse(tutorBlock) : null;
      const from = message.role === "user" ? "child" : "preá";
      return {
        id: message.id,
        seq: message.seq,
        from,
        text,
        tutor: tutor?.success ? tutor.data : null,
        crisis:
          from === "preá" &&
          (blocks.some((block) => block.kind === "crisis") ||
            text === CHILD_CRISIS_TEXT ||
            text === CHILD_UNJUDGED_TEXT),
        refusal: from === "preá" && (text === CHILD_REFUSAL_TEXT || text === CHILD_TRUSTED_ADULT_TEXT),
      } satisfies ChildChatItem;
    });
}

export type ChatPollOptions = {
  /** Highest seq already shown; a reply is done once a Preá message past it exists and the run ended. */
  sinceSeq: number;
  onUpdate: (items: ChildChatItem[], running: boolean) => void;
  intervalMs?: number;
  maxMs?: number;
  signal?: AbortSignal;
  sleep?: (ms: number) => Promise<void>;
};

/**
 * There is no live subscribe on the child routes, so a reply streams by polling the snapshot: each
 * poll shows the text so far, and the loop ends when the run has ended and a reply is in.
 */
export async function pollChatReply(
  api: Pick<ChildChatApi, "get">,
  options: ChatPollOptions,
): Promise<ChildChatItem[]> {
  const sleep =
    options.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  const interval = options.intervalMs ?? 1500;
  const deadline = Date.now() + (options.maxMs ?? 120_000);
  let items: ChildChatItem[] = [];
  while (!options.signal?.aborted) {
    const snapshot = await api.get();
    items = childChatItems(snapshot.messages);
    const running = runIsActive(snapshot.run) || (snapshot.activeRuns ?? []).some(runIsActive);
    const replied = items.some((item) => item.from === "preá" && item.seq > options.sinceSeq);
    options.onUpdate(items, running || !replied);
    if (!running && replied) return items;
    if (Date.now() >= deadline) return items;
    await sleep(interval);
  }
  return items;
}
